// Admin Stories — Instagram-style stories. No auto-expiry: toggle active/inactive.
// Each story can have an optional thumbnail (URL or upload) shown as the ring image.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { chunkedUpload } from '@/lib/chunkedUpload';
import { toast } from 'sonner';
import { Upload, Loader2, Trash2, Eye, Image as ImageIcon, Film, Sparkles, Link as LinkIcon, Power, Pencil } from 'lucide-react';

const PERSONAS = ['all', 'electrician', 'retailer', 'distributor', 'channel_partner', 'technician', 'trainee'];
const LANGS = ['en', 'hi', 'ta', 'ml', 'te', 'kn', 'bn', 'or', 'mr', 'gu'];
const inputCls = 'w-full px-3 py-2 rounded-lg ring-1 ring-slate-300 focus:ring-2 focus:ring-amber-400 focus:outline-none text-sm bg-white';

export default function AdminStories() {
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [preview, setPreview] = useState(null);
    const [editing, setEditing] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const [mode, setMode] = useState('upload'); // 'upload' | 'url'
    const token = typeof window !== 'undefined' ? localStorage.getItem('utc_token') : null;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/admin/stories?include_expired=true');
            setStories(r.data || []);
        } catch {
            toast.error('Failed to load stories');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onUpload = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const fd = new FormData(form);
        if (mode === 'upload') {
            const file = fd.get('file');
            if (!file || file.size === 0) { toast.error('Please choose an image or video'); return; }
            if (file.size > 25 * 1024 * 1024) { toast.error('Max 25 MB.'); return; }
            fd.delete('media_url');
        } else {
            const url = (fd.get('media_url') || '').toString().trim();
            if (!url) { toast.error('Please paste a video / image URL'); return; }
            if (!/^https?:\/\//i.test(url)) { toast.error('URL must start with http(s)://'); return; }
            fd.delete('file');
        }
        // Drop empty thumbnail file so the backend doesn't treat it as an upload
        const tf = fd.get('thumbnail');
        if (tf && tf.size === 0) fd.delete('thumbnail');
        setUploading(true); setProgress(0);
        try {
            if (mode === 'upload') {
                // Chunked upload bypasses proxy body-size limits for large videos/images.
                const file = fd.get('file');
                const up = await chunkedUpload(file, 'story', (p) => setProgress(p));
                fd.delete('file');
                fd.set('upload_id', up.upload_id);
            }
            await api.post('/admin/stories', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Story posted');
            form.reset();
            setProgress(0);
            load();
        } catch (err) {
            toast.error(`Upload failed: ${err?.response?.data?.detail || err.message}`);
        } finally { setUploading(false); }
    };

    const onDelete = async (id) => {
        if (!window.confirm('Delete this story? Viewers lose access immediately.')) return;
        try { await api.delete(`/admin/stories/${id}`); toast.success('Deleted'); load(); }
        catch { toast.error('Delete failed'); }
    };

    const onToggle = async (s) => {
        try {
            await api.patch(`/admin/stories/${s.id}`, { is_active: !s.is_active });
            toast.success(s.is_active ? 'Story deactivated' : 'Story activated');
            load();
        } catch { toast.error('Update failed'); }
    };

    const saveEdit = async () => {
        if (!editing) return;
        setSavingEdit(true);
        try {
            await api.patch(`/admin/stories/${editing.id}`, {
                caption: editing.caption || '',
                language: editing.language || 'en',
                cta_label: editing.cta_label || '',
                cta_url: editing.cta_url || '',
                personas: (editing.personas && editing.personas.length) ? editing.personas : ['all'],
            });
            toast.success('Story updated');
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Update failed');
        } finally { setSavingEdit(false); }
    };

    const active = useMemo(() => stories.filter(s => s.is_active), [stories]);
    const inactive = useMemo(() => stories.filter(s => !s.is_active), [stories]);

    const mediaUrl = (s) => s.is_external ? s.media_url : `${process.env.REACT_APP_BACKEND_URL}${s.media_url}?auth=${encodeURIComponent(token || '')}`;
    const thumbUrl = (s) => !s.thumbnail_url ? null : (s.thumbnail_is_external ? s.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${s.thumbnail_url}?auth=${encodeURIComponent(token || '')}`);

    const renderCard = (s) => (
        <article
            key={s.id}
            className={`group relative aspect-[9/16] rounded-2xl overflow-hidden bg-slate-900 shadow-md cursor-pointer ${s.is_active ? 'ring-2 ring-amber-300' : 'ring-1 ring-slate-300 opacity-70'}`}
            onClick={() => setPreview(s)}
            data-testid={`admin-story-card-${s.id}`}>
            {thumbUrl(s)
                ? <img src={thumbUrl(s)} alt={s.caption || 'Story'} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                : s.media_kind === 'image'
                    ? <img src={mediaUrl(s)} alt={s.caption || 'Story'} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    : s.media_kind === 'embed'
                        ? <iframe src={mediaUrl(s)} title={s.caption || 'Story'} className="absolute inset-0 w-full h-full pointer-events-none" allow="encrypted-media" />
                        : <video src={mediaUrl(s)} className="absolute inset-0 w-full h-full object-cover" muted playsInline />}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
            <div className="absolute top-2 left-2 right-2 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-black/50 text-white">
                    {s.media_kind === 'image' ? <ImageIcon className="w-3 h-3" /> : <Film className="w-3 h-3" />}
                    {s.language?.toUpperCase()}
                </span>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-white ${s.is_active ? 'bg-emerald-500/90' : 'bg-slate-500/90'}`}>
                    {s.is_active ? 'Live' : 'Off'}
                </span>
            </div>
            <div className="absolute bottom-2 left-2 right-2 text-white text-xs">
                <div className="truncate">{s.caption || <span className="italic opacity-70">No caption</span>}</div>
                <div className="flex items-center gap-3 mt-1 text-[10px] opacity-90">
                    <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {s.view_count || 0}</span>
                </div>
            </div>
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                <button type="button" onClick={(e) => { e.stopPropagation(); setEditing({ ...s }); }}
                    className="p-1.5 rounded-lg bg-sky-600 text-white" title="Edit" data-testid={`admin-story-edit-${s.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(s); }}
                    className={`p-1.5 rounded-lg text-white ${s.is_active ? 'bg-slate-700' : 'bg-emerald-600'}`}
                    title={s.is_active ? 'Deactivate' : 'Activate'} data-testid={`admin-story-toggle-${s.id}`}>
                    <Power className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
                    className="p-1.5 rounded-lg bg-rose-600 text-white" title="Delete" data-testid={`admin-story-del-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>
        </article>
    );

    return (
        <div className="space-y-6" data-testid="admin-stories-page">
            <div>
                <h1 className="font-display font-extrabold text-2xl text-slate-900">Stories</h1>
                <p className="text-sm text-slate-500">Short-form Instagram-style updates. Stories stay live until you deactivate them.</p>
            </div>

            {/* Upload form */}
            <form onSubmit={onUpload} className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5 space-y-3" data-testid="admin-story-upload-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input name="caption" placeholder="Caption (max 280 chars)" maxLength={280} className={inputCls} data-testid="su-caption" />
                    <select name="language" defaultValue="en" className={inputCls} data-testid="su-language">
                        {LANGS.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                    </select>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input name="cta_label" placeholder="CTA label (optional)" maxLength={40} className={inputCls} data-testid="su-cta-label" />
                    <input name="cta_url" placeholder="CTA link (https://…)" className={inputCls} data-testid="su-cta-url" />
                    <input name="personas" defaultValue="all" placeholder={`Personas: ${PERSONAS.join(' | ')}`} className={inputCls} data-testid="su-personas" />
                </div>

                {/* Thumbnail (shown as the ring image in the trainee app) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input name="thumbnail_url" placeholder="Thumbnail image URL (optional)" className={inputCls} data-testid="su-thumb-url" />
                    <label className="inline-flex items-center gap-2 text-sm text-slate-600 px-3 py-2 rounded-lg ring-1 ring-slate-300 bg-white">
                        <ImageIcon className="w-4 h-4 text-slate-400" /> Or upload thumbnail
                        <input type="file" name="thumbnail" accept="image/*" className="text-xs" data-testid="su-thumb-file" />
                    </label>
                </div>

                {/* Media source toggle */}
                <div className="inline-flex rounded-lg bg-slate-100 p-1 text-sm font-bold" data-testid="su-mode-toggle">
                    <button type="button" onClick={() => setMode('upload')} data-testid="su-mode-upload"
                        className={`px-3 py-1.5 rounded-md transition inline-flex items-center gap-1.5 ${mode === 'upload' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-800'}`}>
                        <Upload className="w-3.5 h-3.5" /> Upload Video
                    </button>
                    <button type="button" onClick={() => setMode('url')} data-testid="su-mode-url"
                        className={`px-3 py-1.5 rounded-md transition inline-flex items-center gap-1.5 ${mode === 'url' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-800'}`}>
                        <LinkIcon className="w-3.5 h-3.5" /> Video URL
                    </button>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    {mode === 'upload' ? (
                        <input type="file" name="file" accept="image/*,video/mp4,video/webm,video/quicktime" className="text-sm" data-testid="su-file" />
                    ) : (
                        <input type="url" name="media_url" placeholder="Paste video/image URL or YouTube link (https://…)" className={`${inputCls} flex-1 min-w-[260px]`} data-testid="su-media-url" />
                    )}
                    <button type="submit" disabled={uploading}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm disabled:opacity-50"
                        data-testid="su-submit">
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {uploading ? `Uploading ${progress}%` : 'Post Story'}
                    </button>
                    <span className="text-[11px] text-slate-400">
                        {mode === 'upload' ? 'Max 25 MB · Image or video' : 'Direct media link or YouTube URL'}
                    </span>
                </div>
            </form>

            {/* Active */}
            <section className="space-y-2">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    <h2 className="font-bold text-slate-800">Active ({active.length})</h2>
                    {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </div>
                {active.length === 0 && !loading && (
                    <div className="text-sm text-slate-400 py-6 text-center bg-white rounded-2xl ring-1 ring-slate-200">
                        No active stories. Post one above to surface it in the trainee feed.
                    </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {active.map(renderCard)}
                </div>
            </section>

            {/* Inactive */}
            {inactive.length > 0 && (
                <section className="space-y-2">
                    <h2 className="font-bold text-slate-600 text-sm uppercase tracking-wider">Inactive ({inactive.length})</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {inactive.map(renderCard)}
                    </div>
                </section>
            )}

            {/* Edit modal */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setEditing(null)}>
                    <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 ring-1 ring-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-story-edit-modal">
                        <div className="flex items-center gap-2 font-bold text-slate-800"><Pencil className="w-4 h-4" /> Edit story</div>
                        <p className="text-xs text-slate-400">Update the caption, language, CTA and audience. To change the media, post a new story.</p>
                        <textarea value={editing.caption || ''} onChange={(e) => setEditing({ ...editing, caption: e.target.value })}
                            placeholder="Caption" rows="2" maxLength={280} className={inputCls} data-testid="se-caption" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Language</div>
                                <select value={editing.language || 'en'} onChange={(e) => setEditing({ ...editing, language: e.target.value })} className={inputCls} data-testid="se-language">
                                    {LANGS.map(l => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Personas (comma separated)</div>
                                <input value={(editing.personas || []).join(', ')} onChange={(e) => setEditing({ ...editing, personas: e.target.value.split(',').map(x => x.trim()).filter(Boolean) })}
                                    placeholder="all" className={inputCls} data-testid="se-personas" />
                            </label>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input value={editing.cta_label || ''} onChange={(e) => setEditing({ ...editing, cta_label: e.target.value })} placeholder="CTA label (optional)" maxLength={40} className={inputCls} data-testid="se-cta-label" />
                            <input value={editing.cta_url || ''} onChange={(e) => setEditing({ ...editing, cta_url: e.target.value })} placeholder="CTA link (https://…)" className={inputCls} data-testid="se-cta-url" />
                        </div>
                        <div className="flex justify-end gap-2 pt-1">
                            <button onClick={() => setEditing(null)} className="px-3 py-2 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100" data-testid="se-cancel">Cancel</button>
                            <button onClick={saveEdit} disabled={savingEdit}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold text-sm disabled:opacity-50" data-testid="se-save">
                                {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pencil className="w-4 h-4" />} Save changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview modal */}
            {preview && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur grid place-items-center p-4" onClick={() => setPreview(null)}>
                    <div className="relative max-w-xs w-full aspect-[9/16] rounded-2xl overflow-hidden bg-slate-900 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        {preview.media_kind === 'image' ? (
                            <img src={mediaUrl(preview)} className="absolute inset-0 w-full h-full object-cover" alt="" />
                        ) : preview.media_kind === 'embed' ? (
                            <iframe src={mediaUrl(preview)} title="Story preview" className="absolute inset-0 w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
                        ) : (
                            <video src={mediaUrl(preview)} className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline />
                        )}
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent text-white">
                            <div className="font-bold text-sm">{preview.caption || 'No caption'}</div>
                            {preview.cta_label && (
                                <a href={preview.cta_url || '#'} target="_blank" rel="noreferrer"
                                    className="inline-block mt-2 px-3 py-1.5 rounded-lg bg-amber-500 text-slate-900 text-xs font-bold">
                                    {preview.cta_label}
                                </a>
                            )}
                            <div className="text-[10px] mt-2 opacity-80 flex items-center gap-3">
                                <span><Eye className="inline w-3 h-3" /> {preview.view_count || 0}</span>
                                <span>{preview.is_active ? 'Active' : 'Inactive'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
